import { Text, Box, tabNavigationStyle } from '@lojinha/design-system'
import React, { FC } from 'react'
import styled from 'styled-components'
import { formatToKebabCase, testId } from '@lojinha/helpers'

export type TableProps = {
  cols: string[]
  data: Array<{
    label: string
    value: string
    dailyValuePercent: string
  }>
  caption?: string
  footer?: string
  footerHighlight?: string
}

const TableContainer = styled.table`
  width: 100%;
  border: none;

  ${tabNavigationStyle};
`

const Row = styled.tr(
  ({ theme }) => `
    &:nth-child(odd) {
      background: ${theme.colors.gray100};

      ${theme.media.above('md')}{
        td {
          &:first-child {
            border-top-left-radius: ${theme.space.x8};
            border-bottom-left-radius: ${theme.space.x8};
          }
  
          &:last-child{
            border-top-right-radius: ${theme.space.x8};
            border-bottom-right-radius: ${theme.space.x8};
          }
        }
      }
    }
  `
)

export const Table: FC<TableProps> = ({
  cols,
  data,
  caption,
  footer,
  footerHighlight,
}: TableProps) => (
  <TableContainer cellSpacing="0" cellPadding="0" tabIndex={0}>
    <Box
      paddingBottom="x24"
      as="caption"
      px={['x16', 'x16', 'x0']}
      paddingTop={['x40', 'x40', 'x0']}
      {...testId('table-caption')}
    >
      <Text isBrand textTransform="uppercase">
        {caption}
      </Text>
    </Box>
    <thead>
      <tr>
        {cols.map((column, index) => (
          <Box
            key={`${formatToKebabCase(column)}-${index}`}
            as="th"
            paddingBottom="x16"
            paddingLeft={index === 0 ? 'x24' : 'x0'}
            htmlAttrs={{
              scope: 'col',
            }}
          >
            <Text isBold>{column}</Text>
          </Box>
        ))}
      </tr>
    </thead>
    <tbody>
      {data.map((row, index) => (
        <Row key={`${formatToKebabCase(row.label)}-${index}`}>
          <Box as="th" py="x8" px="x24" htmlAttrs={{ scope: 'row' }}>
            <Text textSize="x14">{row.label}</Text>
          </Box>
          <Box as="td" width="33.33%">
            <Text textSize="x14">{row.value}</Text>
          </Box>
          <Box as="td">
            <Text textSize="x14">{row.dailyValuePercent}</Text>
          </Box>
        </Row>
      ))}
    </tbody>
    <tfoot>
      <tr>
        <td colSpan={3}>
          <Box py="x24" px={['x16', 'x16', 'x0']}>
            <Text textSize="x1" color="gray700" {...testId('table-footer')}>
              <strong {...testId('table-footer-highlight')}>
                {footerHighlight}
              </strong>{' '}
              {footer}
            </Text>
          </Box>
        </td>
      </tr>
    </tfoot>
  </TableContainer>
)
